import { getAuth } from 'firebase/auth';
import { getFirebaseApp } from '../lib/firebase';

// import.meta.env.DEV is Vite's own build-mode flag — `true` only for `vite
// dev`/non-production builds, `false` for a real `vite build` regardless of
// which env vars happen to be set — the same non-spoofable-by-missing-config
// guarantee the backend's NODE_ENV === 'development' checks rely on. A
// production build (VITE_API_URL/VITE_FIREBASE_API_KEY genuinely missing due
// to a deployment misconfiguration) must fail as "misconfigured production",
// never silently fall back to dev-token/localhost behavior — that used to
// happen here (both were previously gated on "does VITE_FIREBASE_API_KEY
// look configured", not on the actual build mode).
const IS_DEV = import.meta.env.DEV;

// Development ALWAYS uses the local backend, regardless of VITE_API_URL —
// mirrors the website BFF's getCleaningBackendUrl() safety rule: a stale
// production VITE_API_URL in a developer's local .env must never redirect
// local admin traffic into production. Outside development, a genuinely
// unset VITE_API_URL falls back to a same-origin relative path (not
// localhost, which would be meaningless in a deployed browser) rather than
// throwing at module-load time — an admin build without VITE_API_URL set is
// already broken either way, and a relative-path fallback fails loudly via
// ordinary 404s instead of crashing the whole SPA's initial render.
const API_BASE = IS_DEV ? 'http://localhost:3000' : import.meta.env.VITE_API_URL || '';

async function getAuthToken(): Promise<string | null> {
  // In dev mode, send a dev token derived from the session user
  if (IS_DEV) {
    const saved = sessionStorage.getItem('dev-auth-user');
    if (!saved) return null;
    return `dev-token-${btoa(saved)}`;
  }
  try {
    const app = getFirebaseApp();
    if (!app) return null;
    const auth = getAuth(app);
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const hasJsonBody = options?.body !== undefined && !(options.body instanceof FormData);
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    const e = new Error(error.error || 'Request failed');
    (e as any).data = error; // preserve extra fields (e.g. conflictId) for callers
    throw e;
  }
  return response.json();
}

// Shared by listAssistantObservations (legacy, unpaginated — Billing,
// Assistant → Collected, the sidebar quote-request badge) and
// listAssistantObservationsPaginated (Contacts → Online) — same row shape
// either way, just a different amount of it. Exported so page components
// (Contacts.tsx) can type modal/local state without re-deriving it from
// Awaited<ReturnType<...>>.
export interface AssistantObservation {
  id: number;
  gmailId: string | null;
  fromEmail: string | null;
  fromName: string | null;
  subject: string | null;
  snippet: string | null;
  customerId: number | null;
  customerName: string | null;
  category: string;
  summary: string | null;
  status: string;
  receivedAt: string | null;
  createdAt: string;
  /** Image tokens for a website quote-request observation (see quoteRequests.ts). Empty otherwise. */
  images: string[];
  /** Structured website quote-request fields, when category is 'quote_request'. */
  data: QuoteRequestData | null;
  /** The Xero quote created from this request, if any (null otherwise). */
  quoteId: string | null;
  quotedAt: string | null;
  quoteNumber: string | null;
  quoteStatus: string | null;
  quoteTotal: number | null;
}

// ── Analytics: website quote requests → quotes → accepted/invoiced ──
export interface OnlineQuoteFunnel {
  requests: number; quoted: number; sent: number; accepted: number; invoiced: number;
  declined: number; dismissed: number; pending: number; quotedValue: number; acceptedValue: number;
}
export interface OnlineQuoteAnalytics {
  from: string;
  to: string;
  totals: OnlineQuoteFunnel & { medianHoursToQuote: number | null };
  bySource: Array<OnlineQuoteFunnel & { key: string }>;
  byCampaign: Array<OnlineQuoteFunnel & { key: string }>;
  byService: Array<OnlineQuoteFunnel & { key: string }>;
  byPropertyType: Array<OnlineQuoteFunnel & { key: string }>;
  byWeek: Array<{ weekStart: string; requests: number; quoted: number; accepted: number }>;
  recent: Array<{
    id: number; receivedAt: string; name: string; source: string; services: string[];
    quoteNumber: string | null; quoteId: string | null; quoteStatus: string | null; quoteTotal: number | null;
    outcome: 'pending' | 'dismissed' | 'quoted' | 'sent' | 'accepted' | 'invoiced' | 'declined';
  }>;
}

export interface QuoteRequestData {
  propertyType?: 'residential' | 'commercial';
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  companyName?: string | null;
  floors?: string | null;
  services?: string[];
  serviceAnswers?: Record<string, string>;
  serviceMultiAnswers?: Record<string, string[]>;
  address?: string;
  hearAbout?: string | null;
}

export interface PaginatedObservations {
  items: AssistantObservation[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Fetch an auth-protected SMS media file through a backend proxy path (`src`)
 * and return a blob object URL suitable for an <img src>. The caller must revoke
 * the URL when done. Auth is via the normal bearer token; provider creds stay on
 * the backend.
 */
export async function fetchSmsMediaBlob(src: string): Promise<Blob> {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE}${src}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error('media unavailable');
  return res.blob();
}
export async function fetchSmsMediaObjectUrl(src: string): Promise<string> {
  return URL.createObjectURL(await fetchSmsMediaBlob(src));
}

/** Backend-relative path for one image attached to a quote-request observation
 *  (fetch it with fetchSmsMediaBlob — that helper is just a generic
 *  authenticated-fetch-to-blob, not actually SMS-specific). */
export function observationImagePath(observationId: number, token: string): string {
  return `/assistant/observations/${observationId}/images/${token}`;
}

export interface JobState {
  photos: { id: number; kind: string; contentType: string | null; filename: string | null }[];
  readyToInvoice: boolean; readyAt: string | null; readyBy: string | null;
  startedAt: string | null; startedBy: string | null;
  finishedAt: string | null; finishedBy: string | null;
  note: string | null;
  completionNote: string | null;
}
export interface JobChecklistItem { key: string; label: string; checked: boolean }
export interface JobChecklists {
  firstjob: JobChecklistItem[];
  prestart: JobChecklistItem[];
  preleave: JobChecklistItem[];
  endofday: JobChecklistItem[];
  endofweek: JobChecklistItem[];
  isFirstJobOfDay: boolean;
  isLastJobOfDay: boolean;
  isLastJobOfWeek: boolean;
}
export interface JobCallbackRow {
  id: number; area: string | null; whatWrong: string | null; customerSaid: string | null;
  performedBy: string | null; reviewedBy: string | null; createdAt: string; createdBy: string | null;
  scheduledAt: string | null; appointmentEventId: string | null;
  resolvedAt: string | null; resolvedBy: string | null;
}
export interface JobCallbackInput {
  area?: string | null; whatWrong?: string | null; customerSaid?: string | null;
  performedBy?: string | null; reviewedBy?: string | null;
  scheduledAt?: string | null; durationMins?: number;
}
export interface ServiceLogEntry {
  id: number;
  service_date: string; // YYYY-MM-DD
  hour_meter: number | null;
  item: string;
  interval_months: number | null;
  interval_hours: number | null;
  interval_note: string | null;
  notes: string | null;
  schedule_item_id: number | null;
}
export interface ServiceLogInput {
  service_date: string;
  hour_meter?: number | null;
  item: string;
  interval_months?: number | null;
  interval_hours?: number | null;
  interval_note?: string | null;
  notes?: string | null;
  schedule_item_id?: number | null;
}
export interface MaintenanceScheduleItem {
  id: number;
  item: string;
  action: string | null;
  interval_text: string;
  interval_hours: number | null;
  interval_months: number | null;
  last_done_hours: number | null;
  last_done_date: string | null;
  sort_order: number;
}
export interface MaintenanceScheduleInput {
  item: string;
  action?: string | null;
  interval_text: string;
  interval_hours?: number | null;
  interval_months?: number | null;
  last_done_hours?: number | null;
  last_done_date?: string | null;
  sort_order?: number;
}
export type DueStatus = 'ok' | 'due_soon' | 'overdue' | 'no_baseline' | 'not_tracked';
export interface ScheduleStatusItem extends MaintenanceScheduleItem {
  nextDueHours: number | null;
  nextDueDate: string | null;
  status: DueStatus;
}
export interface MaintenanceStatus {
  usagePct: number;
  baseReadingHours: number | null;
  baseReadingDate: string | null;
  workedHoursSinceReading: number;
  estimatedEngineHours: number | null;
  items: ScheduleStatusItem[];
}

export interface ReferralActivityRow {
  id: number;
  name: string;
  region: string | null;
  relationship_type: string | null;
  active: boolean;
  quotes: number;
  accepted: number;
  lastReferral: string | null;
  daysSinceReferral: number | null;
  dormant: boolean;
}

export const api = {
  // Locations
  getLocations: () => fetchApi<any[]>('/locations'),
  createLocation: (data: any) =>
    fetchApi<any>('/locations', { method: 'POST', body: JSON.stringify(data) }),
  updateLocation: (id: number, data: any) =>
    fetchApi<any>(`/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getOpeningHours: (locationId?: number) =>
    fetchApi<any[]>(locationId ? `/opening-hours?locationId=${locationId}` : '/opening-hours'),
  replaceOpeningHours: (locationId: number, hours: any[]) =>
    fetchApi<any[]>(`/locations/${locationId}/opening-hours`, {
      method: 'PUT',
      body: JSON.stringify({ hours }),
    }),

  // Services
  getServices: () => fetchApi<any[]>('/services'),
  createService: (data: any) =>
    fetchApi<any>('/services', { method: 'POST', body: JSON.stringify(data) }),
  updateService: (id: number, data: any) =>
    fetchApi<any>(`/services/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteService: (id: number) => fetchApi<any>(`/services/${id}`, { method: 'DELETE' }),
  syncServicesWithSquare: () => fetchApi<any>('/services/sync-square', { method: 'POST' }),

  // Contractors
  getContractors: (locationId?: number) =>
    fetchApi<any[]>(locationId ? `/contractors?locationId=${locationId}` : '/contractors'),
  createContractor: (data: { name: string; employment_type?: 'FULL_TIME' | 'PART_TIME' | 'CASUAL' }) =>
    fetchApi<any>('/contractors', { method: 'POST', body: JSON.stringify(data) }),
  updateContractor: (id: number, data: { name: string }) =>
    fetchApi<any>(`/contractors/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteContractor: (id: number) =>
    fetchApi<{ deleted: boolean }>(`/contractors/${id}`, { method: 'DELETE' }),

  // Bookings
  getBookings: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any[]>(`/bookings?${query}`);
  },
  getBooking: (id: number) => fetchApi<any>(`/bookings/${id}`),
  createBooking: (data: any) =>
    fetchApi<any>('/bookings', { method: 'POST', body: JSON.stringify(data) }),
  updateBooking: (id: number, data: any) =>
    fetchApi<any>(`/bookings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  cancelBooking: (id: number) => fetchApi<any>(`/bookings/${id}`, { method: 'DELETE' }),
  removeBooking: (id: number) => fetchApi<any>(`/bookings/${id}/remove`, { method: 'DELETE' }),
  completeBooking: (id: number, data: any) =>
    fetchApi<any>(`/bookings/${id}/complete`, { method: 'POST', body: JSON.stringify(data) }),
  markNoShow: (id: number) =>
    fetchApi<any>(`/bookings/${id}/no-show`, { method: 'POST', body: JSON.stringify({}) }),

  // Square Terminal
  getSquareDevices: (locationId?: number) =>
    fetchApi<{ devices: any[] }>(`/square/devices${locationId ? `?locationId=${locationId}` : ''}`),
  getSquareLocations: () => fetchApi<{ locations: any[] }>('/square/locations'),
  getCatalogItems: () => fetchApi<{ items: any[] }>('/square/catalog-items'),
  createDeviceCode: (locationId: number, name?: string) =>
    fetchApi<{ id: string; code: string; status: string }>('/square/device-codes', {
      method: 'POST',
      body: JSON.stringify({ locationId, name }),
    }),
  getDeviceCodeStatus: (deviceCodeId: string) =>
    fetchApi<{ id: string; code: string; status: string; deviceId?: string }>(
      `/square/device-codes/${deviceCodeId}`,
    ),
  initiateTerminalCheckout: (
    bookingId: string,
    deviceId: string,
    checkoutItems?: { id: string; variationId: string; name: string; price: number; qty: number }[],
    allowTip?: boolean,
  ) =>
    fetchApi<{ checkoutId: string; status: string }>(`/bookings/${bookingId}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, checkoutItems, allowTip }),
    }),
  getTerminalCheckoutStatus: (checkoutId: string) =>
    fetchApi<{ status: string; paymentIds: string[] }>(`/square/terminal-checkout/${checkoutId}`),
  cancelTerminalCheckout: (checkoutId: string) =>
    fetchApi<{ success: boolean }>(`/square/terminal-checkout/${checkoutId}`, { method: 'DELETE' }),
  combinedCheckout: (bookingIds: string[], deviceId: string, allowTip?: boolean) =>
    fetchApi<{ checkoutId: string; status: string }>('/bookings/combined-checkout', {
      method: 'POST',
      body: JSON.stringify({ bookingIds, deviceId, allowTip }),
    }),
  combinedComplete: (bookingIds: string[], paymentMethod: string, squarePaymentId?: string) =>
    fetchApi<any[]>('/bookings/combined-complete', {
      method: 'POST',
      body: JSON.stringify({ bookingIds, paymentMethod, squarePaymentId }),
    }),

  // Reports
  getPerformanceReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/performance?${query}`);
  },
  getFinancialReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/financial?${query}`);
  },
  syncFinancialReport: (data: any) =>
    fetchApi<any>('/reports/financial/sync', { method: 'POST', body: JSON.stringify(data) }),
  reconcileReport: (data: any) =>
    fetchApi<any>('/reports/reconcile', { method: 'POST', body: JSON.stringify(data) }),
  getUtilizationReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/utilization?${query}`);
  },
  getComparisonReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/comparison?${query}`);
  },
  getRevenueReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/revenue?${query}`);
  },
  getWagesReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any[]>(`/reports/wages?${query}`);
  },
  getExpensesReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/expenses?${query}`);
  },
  getPayrollReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/payroll?${query}`);
  },
  getHistoricalOverview: () => fetchApi<any>('/reports/historical/overview'),
  importHistoricalData: (dataDir = 'current-data') =>
    fetchApi<any>('/reports/historical/import', {
      method: 'POST',
      body: JSON.stringify({ dataDir }),
    }),
  getAdTrackingReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/ad-tracking?${query}`);
  },

  // Square cache sync
  getSquareSyncStatus: () => fetchApi<any>('/square/cache/status'),
  startSquareSync: (fromDate: string) =>
    fetchApi<any>('/square/cache/sync', { method: 'POST', body: JSON.stringify({ fromDate }) }),
  exportSquareCache: () => fetchApi<any>('/square/cache/export'),

  createExpense: (data: any) =>
    fetchApi<any>('/expenses', { method: 'POST', body: JSON.stringify(data) }),
  updateExpense: (id: number, data: any) =>
    fetchApi<any>(`/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteExpense: (id: number) => fetchApi<any>(`/expenses/${id}`, { method: 'DELETE' }),
  copyExpensesFromMonth: (data: {
    sourceYear: number;
    sourceMonth: number;
    targetYear: number;
    targetMonth: number;
    locationId?: number;
  }) => fetchApi<any>('/expenses/copy-month', { method: 'POST', body: JSON.stringify(data) }),

  // Inventory
  getInventory: (includeInactive = false) =>
    fetchApi<any[]>(`/inventory${includeInactive ? '?includeInactive=true' : ''}`),
  createInventory: (data: any) =>
    fetchApi<any>('/inventory', { method: 'POST', body: JSON.stringify(data) }),
  updateInventory: (id: number, data: any) =>
    fetchApi<any>(`/inventory/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteInventory: (id: number) => fetchApi<any>(`/inventory/${id}`, { method: 'DELETE' }),
  syncInventoryWithSquare: () => fetchApi<any>('/inventory/sync-square', { method: 'POST' }),

  // Supplies
  getSupplies: (includeInactive = false) =>
    fetchApi<any[]>(`/supplies${includeInactive ? '?includeInactive=true' : ''}`),
  createSupply: (data: any) =>
    fetchApi<any>('/supplies', { method: 'POST', body: JSON.stringify(data) }),
  updateSupply: (id: number, data: any) =>
    fetchApi<any>(`/supplies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteSupply: (id: number) => fetchApi<any>(`/supplies/${id}`, { method: 'DELETE' }),

  // Supply Requests
  getSupplyRequests: (params?: Record<string, string>) => {
    const query = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return fetchApi<{ requests: any[]; total: number }>(
      `/supply-requests${query ? `?${query}` : ''}`,
    );
  },
  createSupplyRequest: (data: any) =>
    fetchApi<any>('/supply-requests', { method: 'POST', body: JSON.stringify(data) }),
  fulfillSupplyRequest: (id: number) =>
    fetchApi<any>(`/supply-requests/${id}/fulfill`, { method: 'POST', body: JSON.stringify({}) }),
  // Fetch a supply-request photo with auth and return an object URL (the
  // endpoint requires a Bearer token, so a plain <img src> can't load it).
  supplyRequestPhotoUrl: async (id: number): Promise<string> => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/supply-requests/${id}/photo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('photo fetch failed');
    return URL.createObjectURL(await res.blob());
  },

  // Customer Notes
  getCustomerNotes: (params: { email?: string; phone?: string; name?: string }) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return fetchApi<{ customerId: number | null; notes: string | null }>(
      `/customer-notes${query ? `?${query}` : ''}`,
    );
  },
  updateCustomerNotes: (data: { email?: string; phone?: string; name?: string; notes: string }) =>
    fetchApi<{ customerId: number; notes: string }>('/customer-notes', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Messaging
  getTemplates: (channel?: string) =>
    fetchApi<any[]>(`/messaging/templates${channel ? `?channel=${channel}` : ''}`),
  getTemplate: (id: number) => fetchApi<any>(`/messaging/templates/${id}`),
  createTemplate: (data: any) =>
    fetchApi<any>('/messaging/templates', { method: 'POST', body: JSON.stringify(data) }),
  updateTemplate: (id: number, data: any) =>
    fetchApi<any>(`/messaging/templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTemplate: (id: number) => fetchApi<any>(`/messaging/templates/${id}`, { method: 'DELETE' }),
  duplicateTemplate: (id: number) =>
    fetchApi<any>(`/messaging/templates/${id}/duplicate`, { method: 'POST' }),
  getContacts: (params: {
    search?: string;
    channel?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) =>
    fetchApi<any>(
      `/messaging/contacts?${new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)]),
      )}`,
    ),
  getContact: (id: number) => fetchApi<any>(`/messaging/contacts/${id}`),
  previewMessage: (data: {
    templateId: number;
    customerId?: number;
    variables?: Record<string, string>;
  }) => fetchApi<any>('/messaging/preview', { method: 'POST', body: JSON.stringify(data) }),
  sendMessage: (data: {
    templateId?: number;
    customerId?: number | null;
    contact?: {
      full_name?: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      mobile?: string;
    };
    variables?: Record<string, string>;
    /** Pass `adhoc` instead of templateId to send a free-text message. */
    adhoc?: {
      channel: 'sms' | 'email';
      subject?: string;
      body: string;
    };
  }) => fetchApi<any>('/messaging/send', { method: 'POST', body: JSON.stringify(data) }),
  getSendLog: (params?: {
    templateId?: number;
    customerId?: number;
    channel?: string;
    status?: string;
    sentBy?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) =>
    fetchApi<any>(
      `/messaging/send-log${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  getMessagingConfig: () => fetchApi<any>('/messaging/config'),
  getInboundMessages: (params?: { limit?: number; offset?: number; unreadOnly?: boolean }) =>
    fetchApi<any>(
      `/messaging/inbound${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  markInboundRead: (ids: number[]) =>
    fetchApi<any>('/messaging/inbound/read', {
      method: 'PATCH',
      body: JSON.stringify({ ids }),
    }),
  getRebookLinks: () => fetchApi<any[]>('/messaging/rebook-links'),
  getScheduledMessages: (params?: {
    status?: string;
    channel?: string;
    limit?: number;
    offset?: number;
  }) =>
    fetchApi<any>(
      `/messaging/schedule${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  scheduleMessage: (data: {
    templateId: number;
    customerId: number;
    variables?: Record<string, string>;
    scheduledFor: string;
  }) => fetchApi<any>('/messaging/schedule', { method: 'POST', body: JSON.stringify(data) }),
  cancelScheduledMessage: (id: number) =>
    fetchApi<any>(`/messaging/schedule/${id}`, { method: 'DELETE' }),
  deleteScheduledMessage: (id: number) =>
    fetchApi<any>(`/messaging/schedule/${id}?hard=true`, { method: 'DELETE' }),
  getContractorUnwellPreview: (contractorId: number, date: string) =>
    fetchApi<{
      templateId: number | null;
      templateName: string;
      templateBody: string | null;
      contractorId: number;
      contractorName: string;
      date: string;
      items: Array<{
        bookingId: string;
        customerName: string;
        mobile: string;
        email: string;
        appointmentDate: string;
        renderedBody: string;
        canSend: boolean;
        skipReason: string | null;
      }>;
    }>(`/messaging/contractor-unwell/preview?contractorId=${contractorId}&date=${date}`),
  sendContractorUnwellBatch: (contractorId: number, date: string) =>
    fetchApi<{ sent: number; failed: number; skipped: number; total: number }>(
      '/messaging/contractor-unwell/send',
      { method: 'POST', body: JSON.stringify({ contractorId, date }) },
    ),
  getBookingReminderPreview: () =>
    fetchApi<{
      templateId: number | null;
      templateName: string;
      templateBody: string | null;
      batches: Array<{
        label: string;
        runAt: string;
        batchSize: number;
        items: Array<{
          bookingId: string;
          customerName: string;
          mobile: string;
          contractorName: string;
          appointmentDate: string;
          renderedBody: string;
        }>;
      }>;
    }>('/messaging/booking-reminder-preview'),
  getRebookingPreview: (limit?: number) =>
    fetchApi<{
      templateId: number | null;
      templateName: string;
      templateBody: string | null;
      nextRunAt: string;
      batchSize: number;
      items: Array<{
        customerId: number;
        customerName: string;
        mobile: string;
        lastAppointmentDate: string | null;
        renderedBody: string;
      }>;
    }>(`/messaging/rebooking-preview${limit ? `?limit=${limit}` : ''}`),

  createContact: (data: {
    full_name: string;
    email?: string | null;
    mobile?: string | null;
    phone?: string | null;
    sms_marketing_status?: string;
    email_marketing_status?: string;
  }) => fetchApi<any>('/messaging/contacts', { method: 'POST', body: JSON.stringify(data) }),

  updateContact: (
    id: number,
    data: {
      full_name?: string;
      email?: string | null;
      mobile?: string | null;
      phone?: string | null;
      sms_marketing_status?: string;
      email_marketing_status?: string;
    },
  ) => fetchApi<any>(`/messaging/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeContact: (id: number, reason?: string) =>
    fetchApi<any>(`/messaging/contacts/${id}/remove`, { method: 'POST', body: JSON.stringify({ reason }) }),
  restoreContact: (id: number) =>
    fetchApi<any>(`/messaging/contacts/${id}/restore`, { method: 'POST' }),
  listRemovalReviews: () => fetchApi<any[]>('/removal-reviews'),
  resolveRemovalReview: (
    id: number,
    opts: { action?: 'remove' | 'none'; verdict?: 'good' | 'bad'; note?: string },
  ) => fetchApi<{ ok: boolean }>(`/removal-reviews/${id}/resolve`, { method: 'POST', body: JSON.stringify(opts) }),

  // Public Holidays
  getPublicHolidays: (params?: { year?: number; locationId?: number }) =>
    fetchApi<any[]>(
      `/public-holidays${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  createPublicHoliday: (data: { name: string; date: string; location_id?: number | null }) =>
    fetchApi<any>('/public-holidays', { method: 'POST', body: JSON.stringify(data) }),
  updatePublicHoliday: (id: number, data: any) =>
    fetchApi<any>(`/public-holidays/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePublicHoliday: (id: number) =>
    fetchApi<any>(`/public-holidays/${id}`, { method: 'DELETE' }),
  copyHolidaysFromYear: (sourceYear: number, targetYear: number) =>
    fetchApi<{ copied: number }>('/public-holidays/copy-year', {
      method: 'POST',
      body: JSON.stringify({ sourceYear, targetYear }),
    }),

  // Xero (payroll) — Slice 1: connection only.
  xeroStartConnect: () => fetchApi<{ authorizeUrl: string }>('/xero/connect', { method: 'POST' }),
  xeroStatus: () =>
    fetchApi<{
      connected: boolean;
      tenants: {
        tenantId: string;
        tenantName: string;
        tenantType: string | null;
        scopes: string;
        tokenExpiresAt: string;
        connectedByEmail: string | null;
        updatedAt: string;
      }[];
    }>('/xero/status'),
  xeroDisconnect: (tenantId: string) =>
    fetchApi<{ ok: true }>(`/xero/tenants/${tenantId}`, { method: 'DELETE' }),

  // Slice 2 — contractor ↔ Xero employee mapping.
  xeroListEmployees: (tenantId: string) =>
    fetchApi<{
      employees: {
        employeeId: string;
        firstName: string;
        lastName: string;
        status: string | null;
        email: string | null;
        classification: string | null;
      }[];
      status: 'ok' | 'no-payroll' | 'reconsent' | 'error';
      error?: string;
    }>(`/xero/payroll/employees?tenantId=${encodeURIComponent(tenantId)}`),
  payrollContractorMappings: (tenantId: string) =>
    fetchApi<{
      mappings: {
        contractorId: number;
        contractorName: string;
        tenantId: string;
        xeroEmployeeId: string | null;
        xeroEmployeeName: string | null;
        mappedByEmail: string | null;
        updatedAt: string | null;
      }[];
    }>(`/payroll/contractor-mappings?tenantId=${encodeURIComponent(tenantId)}`),
  payrollUpsertContractorMapping: (data: {
    contractorId: number;
    tenantId: string;
    xeroEmployeeId: string;
    xeroEmployeeName: string;
  }) =>
    fetchApi<{ ok: true }>(`/payroll/contractor-mappings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  payrollDeleteContractorMapping: (contractorId: number, tenantId: string) =>
    fetchApi<{ ok: true }>(
      `/payroll/contractor-mappings/${contractorId}?tenantId=${encodeURIComponent(tenantId)}`,
      { method: 'DELETE' },
    ),
  payrollTimesheetPreview: (tenantId: string, startDate: string, endDate: string) =>
    fetchApi<{
      tenantId: string;
      startDate: string;
      endDate: string;
      rows: {
        contractorId: number;
        contractorName: string;
        xeroEmployeeId: string;
        xeroEmployeeName: string;
        days: { date: string; hours: number; isSaturday: boolean; isSunday: boolean }[];
        totalHours: number;
        ordinaryHours: number;
        saturdayHours: number;
        sundayHours: number;
      }[];
    }>(
      `/payroll/timesheet/preview?tenantId=${encodeURIComponent(tenantId)}&startDate=${startDate}&endDate=${endDate}`,
    ),
  payrollTimesheetPush: (data: {
    tenantId: string;
    contractorId: number;
    startDate: string;
    endDate: string;
  }) =>
    fetchApi<{
      id: number;
      tenantId: string;
      contractorId: number;
      xeroEmployeeId: string;
      startDate: string;
      endDate: string;
      xeroTimesheetId: string;
      status: string;
      totalHours: number;
    }>(`/payroll/timesheet/push`, { method: 'POST', body: JSON.stringify(data) }),
  payrollTimesheetsPushed: (tenantId: string, startDate: string, endDate: string) =>
    fetchApi<{
      pushed: {
        contractorId: number;
        xeroTimesheetId: string;
        status: string;
        totalHours: number;
        pushedByEmail: string | null;
        updatedAt: string;
        startDate: string;
        endDate: string;
      }[];
    }>(
      `/payroll/timesheet/pushed?tenantId=${encodeURIComponent(tenantId)}&startDate=${startDate}&endDate=${endDate}`,
    ),
  payrollPayCalendars: (tenantId: string) =>
    fetchApi<{
      calendars: {
        payrollCalendarId: string;
        name: string;
        calendarType: string;
        startDate: string;
        paymentDate: string | null;
      }[];
    }>(`/payroll/pay-calendars?tenantId=${encodeURIComponent(tenantId)}`),
  payrollCreatePayRun: (data: {
    tenantId: string;
    payrollCalendarId: string;
    periodEndDate: string;
  }) =>
    fetchApi<{
      id: number;
      tenantId: string;
      payrollCalendarId: string;
      periodStartDate: string;
      periodEndDate: string;
      paymentDate: string | null;
      xeroPayRunId: string;
      status: string;
    }>(`/payroll/pay-runs`, { method: 'POST', body: JSON.stringify(data) }),
  payrollListPayRuns: (tenantId: string) =>
    fetchApi<{
      payRuns: {
        id: number;
        payrollCalendarId: string;
        periodStartDate: string;
        periodEndDate: string;
        paymentDate: string | null;
        xeroPayRunId: string;
        status: string;
        createdByEmail: string | null;
        updatedAt: string;
      }[];
    }>(`/payroll/pay-runs?tenantId=${encodeURIComponent(tenantId)}`),
  payrollLeaveTypes: () => fetchApi<{ leaveTypes: string[] }>(`/payroll/leave/types`),
  payrollListLeave: (filter?: { contractorId?: number; fromDate?: string }) => {
    const qs = new URLSearchParams();
    if (filter?.contractorId) qs.set('contractorId', String(filter.contractorId));
    if (filter?.fromDate) qs.set('fromDate', filter.fromDate);
    const suffix = qs.toString();
    return fetchApi<{
      leave: {
        id: number;
        contractorId: number;
        contractorName: string;
        leaveType: string;
        startDate: string;
        endDate: string;
        description: string | null;
        createdByEmail: string | null;
        createdAt: string;
      }[];
    }>(`/payroll/leave${suffix ? `?${suffix}` : ''}`);
  },
  payrollCreateLeave: (data: {
    contractorId: number;
    leaveType: string;
    startDate: string;
    endDate: string;
    description?: string | null;
  }) =>
    fetchApi<{
      id: number;
      contractorId: number;
      contractorName: string;
      leaveType: string;
      startDate: string;
      endDate: string;
      description: string | null;
      createdByEmail: string | null;
      createdAt: string;
    }>(`/payroll/leave`, { method: 'POST', body: JSON.stringify(data) }),
  payrollDeleteLeave: (id: number) =>
    fetchApi<{ ok: true }>(`/payroll/leave/${id}`, { method: 'DELETE' }),
  payrollWeekly: (dateInWeek: string, tenantId?: string) => {
    const qs = new URLSearchParams({ dateInWeek });
    if (tenantId) qs.set('tenantId', tenantId);
    return fetchApi<{
      startDate: string;
      endDate: string;
      paymentDate: string;
      rows: {
        contractorId: number;
        contractorName: string;
        employmentType: 'FT' | 'PT' | 'CASUAL';
        xeroEmployeeId: string | null;
        xeroEmployeeName: string | null;
        weekdayHours: number;
        saturdayHours: number;
        sundayHours: number;
        // Mon..Sun, exactly 7 entries — `hours: 0` for non-working days.
        days: { date: string; dow: number; hours: number }[];
        weekdayRate: number;
        saturdayRate: number;
        ordinaryEarnings: number;
        tips: number;
        serviceRevenue: number;
        bonusRate: number;
        bonusAmount: number;
        grossPay: number;
        superAmount: number;
      }[];
      totals: {
        weekdayHours: number;
        saturdayHours: number;
        sundayHours: number;
        ordinaryEarnings: number;
        tips: number;
        bonusAmount: number;
        grossPay: number;
        superAmount: number;
      };
      existingPayRun: { xeroPayRunId: string; status: string } | null;
    }>(`/payroll/weekly?${qs.toString()}`);
  },
  payrollRunWeekly: (data: { dateInWeek: string; tenantId: string; paymentDate?: string }) =>
    fetchApi<{
      startDate: string;
      endDate: string;
      paymentDate: string;
      steps: { step: string; ok: boolean; error?: string }[];
      payRunXeroId: string | null;
      payRunStatus: string | null;
      totalGross: number;
      totalSuper: number;
    }>(`/payroll/weekly/run`, { method: 'POST', body: JSON.stringify(data) }),
  payrollGetPayRun: (id: number) =>
    fetchApi<{
      xeroPayRunId: string;
      payrollCalendarId: string;
      periodStartDate: string;
      periodEndDate: string;
      paymentDate: string | null;
      status: string;
      totals: { wages: number; tax: number; super: number; netPay: number };
      stpStatus: 'not-yet-filed' | 'filed-by-xero' | 'unknown';
      payslips: {
        payslipId: string;
        employeeId: string;
        employeeName: string;
        wages: number;
        tax: number;
        super: number;
        reimbursements: number;
        deductions: number;
        netPay: number;
      }[];
    }>(`/payroll/pay-runs/${id}`),

  // Enquiries (ingested inbox + AI draft replies)
  ingestEmails: (max = 25) =>
    fetchApi<{ fetched: number; stored: number; matched: number }>('/emails/ingest', {
      method: 'POST',
      body: JSON.stringify({ max }),
    }),
  listEmails: (matchedOnly = false) =>
    fetchApi<any[]>(`/emails${matchedOnly ? '?matchedOnly=true' : ''}`),
  draftReply: (gmailId: string) =>
    fetchApi<{ draftId: string; to: string; subject: string; body: string; model?: string }>(
      `/emails/${gmailId}/draft`,
      { method: 'POST' },
    ),

  // Triage: understand a raw inbound message (who/what/intent) before drafting.
  understandMessage: (message: string) =>
    fetchApi<{
      intent: string;
      summary: string;
      confidence: 'high' | 'medium' | 'low';
      parties: { from: string | null; about: string | null };
      subject: {
        candidates: { id: number; fullName: string | null; email: string | null; mobile: string | null; matchedOn: string }[];
        resolved: { id: number; fullName: string | null } | null;
      };
      references: {
        quotes: { number: string; docType: string | null; status: string | null; total: string | null; found: boolean }[];
        addresses: string[];
      };
      needsClarification: string | null;
    }>('/understand', { method: 'POST', body: JSON.stringify({ message }) }),

  // Chat-to-context
  contextChat: (message: string) =>
    fetchApi<any>('/context-chat', { method: 'POST', body: JSON.stringify({ message }) }),
  contextChatSave: (data: {
    customerId: number;
    rawMessage: string;
    summary?: string;
    category?: string;
  }) => fetchApi<any>('/context-chat/save', { method: 'POST', body: JSON.stringify(data) }),

  // Knowledge base (context_document) — admin CRUD on the live docs the assistant reads
  getContextDocuments: () => fetchApi<any[]>('/context'),
  saveContextDocument: (doc: {
    slug: string;
    title: string;
    category?: string;
    sensitivity?: string;
    content: string;
  }) => fetchApi<any>('/context', { method: 'POST', body: JSON.stringify(doc) }),
  deleteContextDocument: (slug: string) =>
    fetchApi<{ ok: true }>(`/context/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  // Contacts (customer master)
  listContacts: (params: { search?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return fetchApi<{ contacts: any[]; total: number }>(`/customers${qs ? `?${qs}` : ''}`);
  },
  getCustomerRecord: (id: number) => fetchApi<any>(`/customers/${id}`),
  createCustomerRecord: (data: any) =>
    fetchApi<any>('/customers', { method: 'POST', body: JSON.stringify(data) }),
  updateCustomerRecord: (id: number, data: any) =>
    fetchApi<any>(`/customers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCustomerRecord: (id: number) => fetchApi<any>(`/customers/${id}`, { method: 'DELETE' }),
  mergeCustomerRecord: (keepId: number, mergeId: number) =>
    fetchApi<any>(`/customers/${keepId}/merge`, { method: 'POST', body: JSON.stringify({ mergeId }) }),
  searchXeroContacts: (q: string) =>
    fetchApi<any[]>(`/xero/contacts/search?q=${encodeURIComponent(q)}`),
  getOrCreateCustomerByXeroContact: (xeroContactId: string) =>
    fetchApi<{ id: number }>(`/customers/by-xero-contact/${encodeURIComponent(xeroContactId)}`),
  getXeroItems: () => fetchApi<any[]>(`/xero/items`),
  getArrivalRemindersPreview: (hours = 48) =>
    fetchApi<{ checked: number; sent: number; results: Array<{ eventId: string; title: string | null; firstName: string; channel: string; contact: string; start: string; body?: string }> }>(
      `/calendar/arrival-reminders/preview?hours=${hours}`,
    ),
  getPrewashRemindersPreview: (hours = 336) =>
    fetchApi<{ checked: number; sent: number; results: Array<{ eventId: string; title: string | null; firstName: string; channel: string; contact: string; start: string; body?: string }> }>(
      `/calendar/prewash-reminders/preview?hours=${hours}`,
    ),
  cancelArrivalReminder: (eventId: string) =>
    fetchApi<{ cancelled: boolean }>(`/calendar/arrival-reminders/${encodeURIComponent(eventId)}/cancel`, { method: 'POST' }),
  restoreArrivalReminder: (eventId: string) =>
    fetchApi<{ restored: boolean }>(`/calendar/arrival-reminders/${encodeURIComponent(eventId)}/cancel`, { method: 'DELETE' }),
  cancelPrewashReminder: (eventId: string) =>
    fetchApi<{ cancelled: boolean }>(`/calendar/prewash-reminders/${encodeURIComponent(eventId)}/cancel`, { method: 'POST' }),
  restorePrewashReminder: (eventId: string) =>
    fetchApi<{ restored: boolean }>(`/calendar/prewash-reminders/${encodeURIComponent(eventId)}/cancel`, { method: 'DELETE' }),
  createQuote: (data: {
    customerId: number;
    issueDate: string;
    expiryDate?: string;
    reference?: string;
    /** The website quote request this quote is created from, so the two link. */
    observationId?: number;
    lineItems: Array<{
      itemCode?: string | null;
      description: string;
      quantity: number;
      unitAmount: number;
      accountCode?: string | null;
      taxType?: string | null;
      discountRate?: number | null;
    }>;
  }) => fetchApi<{ ok: boolean; quoteId: string; quoteNumber: string | null; total: number | null }>(`/quotes`, { method: 'POST', body: JSON.stringify(data) }),
  // ── Open / edit / send a quote from the portal ──
  getQuote: (id: string) => fetchApi<any>(`/quotes/${encodeURIComponent(id)}`),
  // ── Analytics: website quote requests, [from, to) as YYYY-MM-DD (Brisbane) ──
  getOnlineQuoteAnalytics: (from: string, to: string) =>
    fetchApi<OnlineQuoteAnalytics>(`/analytics/online-quotes?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  updateQuote: (id: string, data: { reference?: string | null; lineItems: Array<{ itemCode?: string | null; description: string; quantity: number; unitAmount: number; accountCode?: string | null; taxType?: string | null; discountRate?: number | null }> }) =>
    fetchApi<{ quoteId: string; quoteNumber: string | null; total: number | null; status: string | null }>(`/quotes/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) }),
  markQuoteSent: (id: string) =>
    fetchApi<{ status: string | null }>(`/quotes/${encodeURIComponent(id)}/mark-sent`, { method: 'POST' }),
  unacceptQuote: (id: string) =>
    fetchApi<{ status: string | null; removedFromSchedule: number }>(`/quotes/${encodeURIComponent(id)}/unaccept`, { method: 'POST' }),
  duplicateQuote: (id: string) =>
    fetchApi<{ quoteId: string; quoteNumber: string | null }>(`/quotes/${encodeURIComponent(id)}/duplicate`, { method: 'POST' }),
  setQuoteReferralAgent: (id: string, partnerId: number | null) =>
    fetchApi<{ ok: boolean; referralAgent: { partnerId: number; partnerName: string } | null }>(`/quotes/${encodeURIComponent(id)}/referral-agent`, { method: 'PUT', body: JSON.stringify({ partnerId }) }),
  reassignQuoteCustomer: (id: string, data: { name: string; email?: string | null; phone?: string | null; secondName?: string | null; secondEmail?: string | null; secondPhone?: string | null; existingContactId?: string | null }) =>
    fetchApi<{ contactId: string; name: string }>(`/quotes/${encodeURIComponent(id)}/reassign-customer`, { method: 'POST', body: JSON.stringify(data) }),
  getQuotePdfBlob: async (id: string): Promise<Blob> => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/quotes/${encodeURIComponent(id)}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error('Could not download the quote PDF.');
    return res.blob();
  },
  getQuoteScopes: (id: string) =>
    fetchApi<{ scopes: { service: string | null; scope: string; areaM2: number | null }[] }>(`/quotes/${encodeURIComponent(id)}/scopes`),
  saveQuoteScopes: (id: string, data: { quoteNumber?: string | null; scopes: { service?: string | null; scope: string; areaM2?: number | null }[] }) =>
    fetchApi<{ ok: boolean }>(`/quotes/${encodeURIComponent(id)}/scopes`, { method: 'PUT', body: JSON.stringify(data) }),
  getQuoteLineCalc: (id: string) =>
    fetchApi<{ items: { lineIndex: number; calc: any }[] }>(`/quotes/${encodeURIComponent(id)}/line-calc`),
  saveQuoteLineCalc: (id: string, items: { lineIndex: number; calc: any }[]) =>
    fetchApi<{ ok: boolean }>(`/quotes/${encodeURIComponent(id)}/line-calc`, { method: 'PUT', body: JSON.stringify({ items }) }),
  getFollowupDraft: (id: string, channel: 'email' | 'sms' = 'email') =>
    fetchApi<{ to: string | null; subject: string; body: string; channel: 'email' | 'sms' }>(`/quotes/${encodeURIComponent(id)}/followup?channel=${channel}`),
  sendFollowup: (id: string, channel: 'email' | 'sms', message: string) =>
    fetchApi<{ sent: boolean; to: string | null; error?: string }>(`/quotes/${encodeURIComponent(id)}/followup`, { method: 'POST', body: JSON.stringify({ channel, message }) }),
  getFollowupTemplate: (channel: 'email' | 'sms' = 'email') =>
    fetchApi<{ subject: string; body: string }>(`/quotes/followup-template?channel=${channel}`),
  saveFollowupTemplate: (data: { channel: 'email' | 'sms'; subject: string; body: string }) =>
    fetchApi<{ subject: string; body: string }>('/quotes/followup-template', { method: 'PUT', body: JSON.stringify(data) }),
  declineQuote: (id: string) =>
    fetchApi<{ status: string | null }>(`/quotes/${encodeURIComponent(id)}/decline`, { method: 'POST' }),
  getQuoteFollowupPreview: () =>
    fetchApi<{
      queue: { quoteId: string; quoteNumber: string; contactName: string; docDate: string; channel: 'email' | 'sms'; recipient: string; reason: string; alreadyQueued: boolean; status: string | null; scheduleId: number | null; sendAt: string; subject: string | null; body: string }[];
      skipped: { quoteId: string; quoteNumber: string; contactName: string; reason: string }[];
      slot: string;
    }>('/quotes/followup-schedule/preview'),
  runQuoteFollowups: () =>
    fetchApi<{ queued: number; email: number; sms: number; skipped: number; noTemplate: boolean }>('/quotes/followup-schedule/run', { method: 'POST' }),
  saveQuoteLink: (id: string, onlineUrl: string) =>
    fetchApi<{ ok: boolean }>(`/quotes/${encodeURIComponent(id)}/online-link`, { method: 'PUT', body: JSON.stringify({ onlineUrl }) }),
  getQuoteSendPreview: (id: string, onlineUrl?: string | null) =>
    fetchApi<{
      quoteId: string; quoteNumber: string | null; contactName: string | null;
      email: string | null; mobile: string | null; onlineUrl: string | null;
      emailSubject: string; emailBody: string; smsBody: string;
      thread: { found: boolean; subject: string | null; date: string | null; threadId: string | null };
      pdfAvailable: boolean;
    }>(`/quotes/${encodeURIComponent(id)}/send-preview`, { method: 'POST', body: JSON.stringify({ onlineUrl: onlineUrl ?? null }) }),
  sendQuote: (id: string, data: { toEmail?: string | null; toMobile?: string | null; emailSubject?: string; emailBody?: string; smsBody?: string; onlineUrl?: string | null; threadId?: string | null; customerId?: number | null }) =>
    fetchApi<{ ok: boolean; email: { attempted: boolean; sent: boolean; error?: string }; sms: { attempted: boolean; sent: boolean; error?: string } }>(`/quotes/${encodeURIComponent(id)}/send`, { method: 'POST', body: JSON.stringify(data) }),
  // ── Open / send an invoice from the portal (mirrors the quote-send flow) ──
  getInvoice: (id: string) => fetchApi<any>(`/invoices/${encodeURIComponent(id)}`),
  markInvoiceSent: (id: string) =>
    fetchApi<{ status: string | null; sentToContact: boolean }>(`/invoices/${encodeURIComponent(id)}/mark-sent`, { method: 'POST' }),
  saveInvoiceLink: (id: string, onlineUrl: string) =>
    fetchApi<{ ok: boolean }>(`/invoices/${encodeURIComponent(id)}/online-link`, { method: 'PUT', body: JSON.stringify({ onlineUrl }) }),
  // Pass onlineUrl to override the link (empty string = "no link"); omit it to let
  // the backend auto-fetch Xero's online-invoice URL.
  getInvoiceSendPreview: (id: string, onlineUrl?: string | null) =>
    fetchApi<{
      invoiceId: string; invoiceNumber: string | null; contactName: string | null;
      email: string | null; mobile: string | null; onlineUrl: string | null;
      emailSubject: string; emailBody: string; smsBody: string;
      thread: { found: boolean; subject: string | null; date: string | null; threadId: string | null };
      pdfAvailable: boolean;
    }>(`/invoices/${encodeURIComponent(id)}/send-preview`, { method: 'POST', body: JSON.stringify(onlineUrl === undefined ? {} : { onlineUrl }) }),
  sendInvoice: (id: string, data: { toEmail?: string | null; toMobile?: string | null; emailSubject?: string; emailBody?: string; smsBody?: string; onlineUrl?: string | null; threadId?: string | null; customerId?: number | null }) =>
    fetchApi<{ ok: boolean; email: { attempted: boolean; sent: boolean; error?: string }; sms: { attempted: boolean; sent: boolean; error?: string } }>(`/invoices/${encodeURIComponent(id)}/send`, { method: 'POST', body: JSON.stringify(data) }),
  linkContactXero: (id: number, xeroContactId: string | null) =>
    fetchApi<any>(`/customers/${id}/xero-link`, { method: 'PATCH', body: JSON.stringify({ xeroContactId }) }),
  setArrivalChannel: (id: number, arrivalChannel: 'sms' | 'email' | null) =>
    fetchApi<{ ok: boolean; arrivalChannel: 'sms' | 'email' | null }>(`/customers/${id}/arrival-channel`, { method: 'PATCH', body: JSON.stringify({ arrivalChannel }) }),
  addCustomerNote: (id: number, data: { message?: string; attachmentIds?: number[] }) =>
    fetchApi<any>(`/customers/${id}/note`, { method: 'POST', body: JSON.stringify(data) }),
  // Load a contact's persisted assistant conversation (memory).
  getCustomerConversation: (id: number) =>
    fetchApi<{ role: 'user' | 'assistant'; content: string; createdAt: string }[]>(
      `/customers/${id}/conversation`,
    ),
  // Ask the per-contact assistant (grounded in business knowledge + this customer).
  customerAssistant: (id: number, data: { message: string }) =>
    fetchApi<{
      reply: string;
      model?: string;
      contextUsed?: {
        knowledgeBase: string[];
        customer: { name: string | null; counts: { quotes: number; invoices: number; bookings: number; notes: number } } | null;
      };
    }>(`/customers/${id}/assistant`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  assistantChatFeedback: (
    id: number,
    data: { verdict: 'good' | 'bad'; question?: string; answer?: string; note?: string },
  ) => fetchApi<{ ok: boolean }>(`/customers/${id}/assistant/feedback`, { method: 'POST', body: JSON.stringify(data) }),
  uploadAttachment: (id: number, data: { filename: string; mimeType: string; dataBase64: string }) =>
    fetchApi<{ id: number }>(`/customers/${id}/attachments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  // Fetch an attachment with auth and return an object URL for <img src>.
  attachmentObjectUrl: async (attachmentId: number): Promise<string> => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/attachments/${attachmentId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('attachment fetch failed');
    return URL.createObjectURL(await res.blob());
  },
  // Fetch a contractor-invoice file with auth and return an object URL.
  // The endpoint requires a Bearer token, so a plain <a href> can't open it.
  contractorInvoiceFileUrl: async (id: number): Promise<string> => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/contractor-invoices/${id}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('invoice file fetch failed');
    return URL.createObjectURL(await res.blob());
  },

  // Xero billing (quotes & invoices)
  billingSync: () =>
    fetchApi<{ tenantId: string; quotes: number; invoices: number; warnings: string[] }>(
      '/billing/sync',
      { method: 'POST' },
    ),
  billingScheduled: () =>
    fetchApi<
      {
        bookingId: string;
        start: string;
        end: string;
        customerName: string | null;
        customerEmail: string | null;
        address: string | null;
        docNumber: string | null;
        docId: string | null;
        docType: 'quote' | 'invoice' | null;
        value: number;
        preWashSentAt: string | null;
        daysUntil: number;
      }[]
    >('/billing/scheduled'),
  sendPreWashReminder: (bookingId: string) =>
    fetchApi<{ sent: boolean; to?: string; date?: string; window?: string; error?: string }>(
      `/billing/scheduled/${encodeURIComponent(bookingId)}/pre-wash`,
      { method: 'POST' },
    ),
  acceptAndBookQuote: (
    quoteId: string,
    data: { startTime: string; durationMins?: number; summary?: string; location?: string | null },
  ) =>
    fetchApi<{ bookingId: string; days?: number; calendar?: { success: boolean; error?: string } }>(
      `/billing/quotes/${encodeURIComponent(quoteId)}/accept-and-book`,
      { method: 'POST', body: JSON.stringify(data) },
    ),
  billingQuotes: () => fetchApi<any[]>('/billing/quotes'),
  draftQuoteCount: () => fetchApi<{ count: number }>('/billing/quotes/draft-count'),
  billingInvoices: () => fetchApi<any[]>('/billing/invoices'),
  billingUnscheduled: () =>
    fetchApi<
      {
        customerId: number | null;
        contactId: string;
        contactName: string | null;
        email: string | null;
        mobile: string | null;
        address: string | null;
        status: string;
        value: number;
        latestDocDate: string | null;
        latestDocId: string | null;
        latestDocNumber: string | null;
        latestDocType: 'quote' | 'invoice' | null;
      }[]
    >('/billing/unscheduled'),
  billingRemind: (customerId: number) =>
    fetchApi<any>(`/billing/unscheduled/${customerId}/remind`, { method: 'POST' }),
  calendarJobs: (from: string, to: string) =>
    fetchApi<
      {
        id: string;
        start_time: string;
        end_time: string;
        customer_name: string | null;
        customer_phone: string | null;
        status: string;
        google_event_id: string | null;
        address: string | null;
        description?: string | null;
        html_link?: string | null;
        all_day?: boolean;
        source?: string;
      }[]
    >(`/calendar/jobs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  calendarDeleteEvent: (eventId: string) =>
    fetchApi<{ deleted: boolean; bookingRemoved: boolean }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}`,
      { method: 'DELETE' },
    ),
  calendarScheduleMessage: (weekStart: string) =>
    fetchApi<{ text: string }>(`/calendar/schedule-message?weekStart=${encodeURIComponent(weekStart)}`),
  calendarReschedule: (eventId: string, data: { startTime: string; durationMins: number }) =>
    fetchApi<{ success: boolean; error?: string }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/reschedule`,
      { method: 'POST', body: JSON.stringify(data) },
    ),
  setJobAddress: (eventId: string, address: string) =>
    fetchApi<{ ok: boolean; address: string }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/location`,
      { method: 'POST', body: JSON.stringify({ address }) },
    ),
  splitJob: (eventId: string, data: { firstEnd: string; continueStart: string; continueEnd: string }) =>
    fetchApi<{ success: boolean; newEventId?: string; error?: string }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/split`,
      { method: 'POST', body: JSON.stringify(data) },
    ),
  // Merge a multi-day job (Day 1/Day 2…) back into a single job.
  mergeJobDays: (eventId: string) =>
    fetchApi<{ success: boolean; mergedDays?: number; error?: string }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/merge-days`,
      { method: 'POST' },
    ),
  getOrphanedJobData: (eventId: string) =>
    fetchApi<{ oldEventIds: string[]; photos: number; scope: number }>(`/calendar/jobs/${encodeURIComponent(eventId)}/orphaned`),
  recoverJobData: (eventId: string) =>
    fetchApi<{ photos: number; scope: number }>(`/calendar/jobs/${encodeURIComponent(eventId)}/recover`, { method: 'POST' }),
  getJobScope: (eventId: string, kind: 'scope' | 'defect' = 'scope') =>
    fetchApi<{ items: { id: number; body: string; done: boolean; position: number }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/scope?kind=${kind}`),
  addJobScopeItem: (eventId: string, body: string, kind: 'scope' | 'defect' = 'scope') =>
    fetchApi<{ items: { id: number; body: string; done: boolean; position: number }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/scope`, { method: 'POST', body: JSON.stringify({ body, kind }) }),
  updateJobScopeItem: (eventId: string, id: number, patch: { body?: string; done?: boolean }, kind: 'scope' | 'defect' = 'scope') =>
    fetchApi<{ items: { id: number; body: string; done: boolean; position: number }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/scope/${id}?kind=${kind}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  removeJobScopeItem: (eventId: string, id: number, kind: 'scope' | 'defect' = 'scope') =>
    fetchApi<{ items: { id: number; body: string; done: boolean; position: number }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/scope/${id}?kind=${kind}`, { method: 'DELETE' }),
  addJobScopeFromVoice: (eventId: string, audioBase64: string, mime: string) =>
    fetchApi<{ items: { id: number; body: string; done: boolean; position: number }[]; added: number; transcript?: string }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/scope/voice`, { method: 'POST', body: JSON.stringify({ audioBase64, mime }) }),
  getJobState: (eventId: string) =>
    fetchApi<JobState>(`/calendar/jobs/${encodeURIComponent(eventId)}/state`),
  uploadJobPhotos: (eventId: string, images: { base64: string; mime: string; filename?: string }[], kind: 'before' | 'after' | 'callback' | 'note' | 'issue' = 'after') =>
    fetchApi<{ photos: { id: number; kind: string; contentType: string | null; filename: string | null }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/photos`, { method: 'POST', body: JSON.stringify({ images, kind }) }),
  removeJobPhoto: (eventId: string, id: number) =>
    fetchApi<{ photos: { id: number; kind: string; contentType: string | null }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/photos/${id}`, { method: 'DELETE' }),
  setJobReady: (eventId: string, ready: boolean) =>
    fetchApi<JobState>(`/calendar/jobs/${encodeURIComponent(eventId)}/ready`, { method: 'POST', body: JSON.stringify({ ready }) }),
  startJob: (eventId: string) =>
    fetchApi<JobState>(`/calendar/jobs/${encodeURIComponent(eventId)}/start`, { method: 'POST' }),
  finishJob: (eventId: string, finished: boolean) =>
    fetchApi<JobState>(`/calendar/jobs/${encodeURIComponent(eventId)}/finish`, { method: 'POST', body: JSON.stringify({ finished }) }),
  getJobChecklists: (eventId: string) =>
    fetchApi<JobChecklists>(`/calendar/jobs/${encodeURIComponent(eventId)}/checklists`),
  setJobChecklistItem: (eventId: string, phase: 'firstjob' | 'prestart' | 'preleave' | 'endofday' | 'endofweek', itemKey: string, checked: boolean) =>
    fetchApi<JobChecklists>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/checklists`, { method: 'POST', body: JSON.stringify({ phase, itemKey, checked }) }),
  setJobNote: (eventId: string, note: string) =>
    fetchApi<JobState>(`/calendar/jobs/${encodeURIComponent(eventId)}/note`, { method: 'PUT', body: JSON.stringify({ note }) }),
  setJobCompletionNote: (eventId: string, note: string) =>
    fetchApi<JobState>(`/calendar/jobs/${encodeURIComponent(eventId)}/completion-note`, { method: 'PUT', body: JSON.stringify({ note }) }),
  // Callbacks (return visits)
  listJobCallbacks: (eventId: string) =>
    fetchApi<{ callbacks: JobCallbackRow[] }>(`/calendar/jobs/${encodeURIComponent(eventId)}/callbacks`),
  createJobCallback: (eventId: string, data: JobCallbackInput) =>
    fetchApi<{ callback: JobCallbackRow; callbacks: JobCallbackRow[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/callbacks`, { method: 'POST', body: JSON.stringify(data) }),
  updateJobCallback: (eventId: string, id: number, patch: JobCallbackInput) =>
    fetchApi<{ callbacks: JobCallbackRow[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/callbacks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  removeJobCallback: (eventId: string, id: number) =>
    fetchApi<{ callbacks: JobCallbackRow[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/callbacks/${id}`, { method: 'DELETE' }),
  listJobCallbackPhotos: (eventId: string, callbackId: number) =>
    fetchApi<{ photos: { id: number; kind: string; contentType: string | null }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/callbacks/${callbackId}/photos`),
  uploadJobCallbackPhotos: (eventId: string, callbackId: number, images: { base64: string; mime: string }[]) =>
    fetchApi<{ photos: { id: number; kind: string; contentType: string | null }[] }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/callbacks/${callbackId}/photos`, { method: 'POST', body: JSON.stringify({ images }) }),
  calendarSync: () =>
    fetchApi<{ imported: number; updated: number; removed: number; pushed: number; enriched: number; error?: string }>(
      '/calendar/sync',
      { method: 'POST' },
    ),
  reportsWeekly: (weekStart: string) =>
    fetchApi<{
      weekStart: string; weekEnd: string;
      revenue: number; gst: number; contractorPay: number;
      jobs: number; perJobCost: number; jobCost: number;
      monthlyStorage: number; storageWeekly: number; profit: number;
      actualExpenses: number; expensesBreakdown: { category: string; total: number }[];
      amortisation: { ratePerJob: number; jobsThisWeek: number; amortised: number; jobsInPeriod: number; since: string | null };
      expectedRevenue: number; expectedGst: number; projectedProfit: number; marginPct: number;
      jobsBreakdown: { customer: string | null; docNumber: string | null; value: number }[];
      paidInvoices: { number: string | null; contact: string | null; date: string; total: number }[];
      contractorInvoices: { id: number; contractorId: number | null; name: string | null; amount: number | null; status: string; source: string }[];
    }>(`/reports/weekly?weekStart=${encodeURIComponent(weekStart)}`),
  reportsDashboard: (from: string, to: string) =>
    fetchApi<{
      from: string; to: string;
      pipeline: { newEnquiries: number; quotesSent: number; quotesAccepted: number; quoteConversionPct: number | null; avgJobValue: number };
      distribution: { newPartnersContacted: number; activeReferralPartners: number };
      customers: { reactivationMessagesSent: number; repeatBookings: number };
      operations: { jobsCompleted: number; reworkPct: number | null; revenue: number; grossProfit: number; grossMarginPct: number | null };
    }>(`/reports/dashboard?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  listXeroExpenses: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return fetchApi<
      {
        xeroId: string; number: string | null; contactName: string | null; docDate: string | null;
        total: number; status: string | null; isExpense: boolean; category: string | null; reviewedAt: string | null;
        source: string; likelyContractor: boolean;
      }[]
    >(`/xero-expenses${q ? `?${q}` : ''}`);
  },
  categorizeXeroExpense: (xeroId: string, patch: { isExpense?: boolean; category?: string }) =>
    fetchApi<{ ok: boolean }>(`/xero-expenses/${encodeURIComponent(xeroId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  listExpenseCategories: () =>
    fetchApi<{ name: string; allocatePerJob: boolean }[]>('/expense-categories'),
  setExpenseCategoryAllocation: (name: string, allocatePerJob: boolean) =>
    fetchApi<{ ok: boolean }>(`/expense-categories/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      body: JSON.stringify({ allocatePerJob }),
    }),
  listAssistantObservations: (status: 'new' | 'archived' | 'all' = 'new') =>
    fetchApi<AssistantObservation[]>(`/assistant/observations?status=${status}`),
  // Category-filtered, fully-paginated observations feed — used by Contacts →
  // Online (Pending/Dismissed Quick Message lists). Unlike
  // listAssistantObservations above (which the legacy consumers — Billing,
  // Assistant → Collected, the sidebar quote-request badge — keep using
  // unchanged), this has no 200-row visibility ceiling: category filtering
  // happens server-side, in SQL, before pagination (see
  // assistant.ts#listObservationsPaginated on the backend).
  //
  // `source: 'website'` additionally restricts to gmail_id IS NULL — needed
  // for 'quote_request', which is used by BOTH website Quote Tool
  // submissions (Billing → New Quote Requests) and Gmail-scanned quote
  // emails; without it, the server would return both mixed together. Also
  // mapped/validated server-side (assistant.ts#parseObservationsPageQuery) —
  // 'website' is the only accepted value, not arbitrary filtering.
  listAssistantObservationsPaginated: (params: { status: 'new' | 'archived'; category: string; page: number; pageSize: number; source?: 'website' }) =>
    fetchApi<PaginatedObservations>(
      `/assistant/observations?status=${encodeURIComponent(params.status)}&category=${encodeURIComponent(params.category)}&page=${params.page}&pageSize=${params.pageSize}${params.source ? `&source=${encodeURIComponent(params.source)}` : ''}`,
    ),
  archiveAssistantObservation: (id: number) =>
    fetchApi<{ ok: boolean }>(`/assistant/observations/${id}/archive`, { method: 'POST' }),
  resolveOrCreateCustomer: (data: { name?: string | null; email?: string | null; mobile?: string | null }) =>
    fetchApi<{ id: number; created: boolean }>('/customers/resolve-or-create', { method: 'POST', body: JSON.stringify(data) }),
  listAssistantDrafts: (status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending') =>
    fetchApi<{
      pending: number;
      items: {
        id: number; fromEmail: string | null; fromName: string | null; subject: string | null; snippet: string | null;
        customerId: number | null; customerName: string | null; category: string; summary: string | null;
        draftReply: string | null; draftStatus: string; correction: string | null; receivedAt: string | null;
      }[];
    }>(`/assistant/drafts?status=${status}`),
  reviewAssistantDraft: (
    id: number,
    body: { verdict: 'approve' | 'reject'; draft?: string; correction?: string; send?: boolean },
  ) => fetchApi<{ ok: boolean; sent?: boolean }>(`/assistant/drafts/${id}/review`, { method: 'POST', body: JSON.stringify(body) }),
  dismissAssistantDraft: (id: number) =>
    fetchApi<{ ok: boolean }>(`/assistant/drafts/${id}/dismiss`, { method: 'POST' }),
  // ─── SMS Assistant ───
  listSmsDrafts: (status: 'pending' | 'approved' | 'rejected' | 'sent' | 'all' = 'pending') =>
    fetchApi<{
      pending: number;
      items: {
        id: number; phone: string; customerId: number | null; customerName: string | null;
        draftReply: string | null; draftStatus: string; correction: string | null;
        inboundBody: string | null; receivedAt: string | null; createdAt: string;
      }[];
    }>(`/assistant/sms/drafts?status=${status}`),
  reviewSmsDraft: (
    id: number,
    body: { verdict: 'approve' | 'reject'; draft?: string; correction?: string; send?: boolean },
  ) => fetchApi<{ ok: boolean; sent?: boolean }>(`/assistant/sms/drafts/${id}/review`, { method: 'POST', body: JSON.stringify(body) }),
  dismissSmsDraft: (id: number) =>
    fetchApi<{ ok: boolean }>(`/assistant/sms/drafts/${id}/dismiss`, { method: 'POST' }),
  listSmsThreads: (search?: string) =>
    fetchApi<{
      threads: {
        phone: string; customerId: number | null; customerName: string | null;
        lastAt: string; lastBody: string; lastDirection: 'in' | 'out'; unread: number;
        messages: { direction: 'in' | 'out'; body: string; at: string; status?: string | null; read?: boolean; media?: { src: string; contentType: string; filename?: string | null }[] }[];
      }[];
    }>(`/assistant/sms/threads${search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`),
  getSmsThread: (phone: string) =>
    fetchApi<{
      phone: string; customerId: number | null; customerName: string | null;
      messages: { direction: 'in' | 'out'; body: string; at: string; status?: string | null; read?: boolean; media?: { src: string; contentType: string; filename?: string | null }[] }[];
    } | null>(`/assistant/sms/threads/${encodeURIComponent(phone)}`),
  markSmsThreadRead: (phone: string) =>
    fetchApi<{ ok: boolean }>(`/assistant/sms/threads/${encodeURIComponent(phone)}/read`, { method: 'POST' }),
  sendOutboundSms: (data: { phone: string; customerId?: number | null; body: string; images?: { base64: string; mime: string; name?: string }[]; force?: boolean }) =>
    fetchApi<{ ok: boolean; sent?: boolean; viaLink?: boolean; unsubscribed?: boolean; error?: string }>(`/assistant/sms/send`, { method: 'POST', body: JSON.stringify(data) }),
  composeSmsDraft: (customerId: number, instruction?: string) =>
    fetchApi<{ draft: string }>(`/assistant/sms/compose-draft`, { method: 'POST', body: JSON.stringify({ customerId, instruction }) }),
  backfillSms: () =>
    fetchApi<{ fetched: number; imported: number; skipped: number }>(`/assistant/sms/backfill`, { method: 'POST', body: JSON.stringify({ limit: 100 }) }),
  // ─── Voice ───
  getVoiceToken: () => fetchApi<{ token: string; identity: string }>(`/voice/token`),
  lookupVoiceCaller: (number: string) =>
    fetchApi<{ customerId: number | null; name: string | null }>(`/voice/lookup?number=${encodeURIComponent(number)}`),
  getVoiceForwarding: () =>
    fetchApi<{ enabled: boolean; numbers: { id: number; e164: string; label: string | null }[] }>(`/voice/forwarding`),
  setVoiceForwardingEnabled: (enabled: boolean) =>
    fetchApi<{ enabled: boolean; numbers: { id: number; e164: string; label: string | null }[] }>(`/voice/forwarding`, {
      method: 'PATCH', body: JSON.stringify({ enabled }),
    }),
  addVoiceForwardNumber: (number: string, label?: string) =>
    fetchApi<{ enabled: boolean; numbers: { id: number; e164: string; label: string | null }[] }>(`/voice/forwarding/numbers`, {
      method: 'POST', body: JSON.stringify({ number, label }),
    }),
  removeVoiceForwardNumber: (id: number) =>
    fetchApi<{ enabled: boolean; numbers: { id: number; e164: string; label: string | null }[] }>(`/voice/forwarding/numbers/${id}`, {
      method: 'DELETE',
    }),
  listVoiceCalls: () =>
    fetchApi<{
      missed: number;
      calls: {
        id: number; callSid: string | null; direction: 'in' | 'out'; fromNumber: string | null; toNumber: string | null;
        customerId: number | null; customerName: string | null; status: string | null; missed: boolean;
        durationSeconds: number | null; voicemailUrl: string | null; createdAt: string;
      }[];
    }>(`/voice/calls`),
  reportsSetSettings: (data: { perJobCost?: number; monthlyStorage?: number }) =>
    fetchApi<{ perJobCost: number; monthlyStorage: number }>(`/reports/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  contractorInvoices: (weekStart: string) =>
    fetchApi<
      {
        id: number; contractorId: number | null; name: string | null; weekStart: string;
        amount: number | null; description: string | null; source: string; status: string;
        gmailFrom: string | null; gmailSubject: string | null; gmailDate: string | null;
        filename: string | null; hasFile: boolean; createdAt: string;
      }[]
    >(`/contractor-invoices?weekStart=${encodeURIComponent(weekStart)}`),
  contractorInvoiceCreate: (data: {
    contractorId?: number | null; weekStart: string; amount?: number | null; description?: string | null;
    fileBase64?: string; filename?: string; mimeType?: string;
  }) => fetchApi<{ id: number }>(`/contractor-invoices`, { method: 'POST', body: JSON.stringify(data) }),
  contractorInvoiceUpdate: (id: number, patch: { contractorId?: number | null; amount?: number | null; status?: string }) =>
    fetchApi<{ ok: boolean }>(`/contractor-invoices/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  contractorInvoiceDelete: (id: number) =>
    fetchApi<{ ok: boolean }>(`/contractor-invoices/${id}`, { method: 'DELETE' }),
  contractorInvoicesFetchGmail: (weekStart: string) =>
    fetchApi<{ created: number; needsReview: number; scanned: number; error?: string }>(
      `/contractor-invoices/fetch-gmail`,
      { method: 'POST', body: JSON.stringify({ weekStart }) },
    ),
  availabilityWeek: (weekStart: string) =>
    fetchApi<
      {
        contractorId: number;
        name: string;
        services: string[];
        defaultDows: number[];
        days: { date: string; dow: number; available: boolean; source: 'default' | 'override' }[];
      }[]
    >(`/contractor-availability?weekStart=${encodeURIComponent(weekStart)}`),
  availabilitySetOverride: (data: { contractorId?: number; day: string; available: boolean }) =>
    fetchApi<{ ok: boolean }>(`/contractor-availability/override`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  availabilityClearOverride: (data: { contractorId?: number; day: string }) =>
    fetchApi<{ ok: boolean }>(`/contractor-availability/override`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    }),
  availabilitySetDefault: (data: { contractorId?: number; dows: number[] }) =>
    fetchApi<{ ok: boolean }>(`/contractor-availability/default`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  availabilityCopyWeek: (data: { fromWeekStart: string; toWeekStart: string; contractorId?: number }) =>
    fetchApi<{ copied: number }>(`/contractor-availability/copy-week`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  calendarCompleteJob: (
    eventId: string,
    photos: { filename: string; mimeType: string; dataBase64: string }[],
    verdict: 'up' | 'down',
  ) =>
    fetchApi<{
      success: boolean;
      error?: string;
      invoiceNumber?: string | null;
      invoiceUrl?: string | null;
      total?: number | null;
      photosAttached?: number;
      emailed?: boolean;
      emailSkippedReason?: string | null;
      smsSent?: boolean;
      smsSkippedReason?: string | null;
    }>(`/calendar/jobs/${encodeURIComponent(eventId)}/complete`, {
      method: 'POST',
      body: JSON.stringify({ photos, verdict }),
    }),
  // Mark a job complete WITHOUT invoicing (already invoiced in the portal or
  // externally). Closes the job + keeps metrics; no second invoice.
  calendarCompleteNoInvoice: (eventId: string, verdict: 'up' | 'down') =>
    fetchApi<{ success: boolean; error?: string }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/complete-no-invoice`,
      { method: 'POST', body: JSON.stringify({ verdict }) },
    ),
  // Job quality verdict + whether the job has an open callback (drives the gate).
  getJobQuality: (eventId: string) =>
    fetchApi<{ verdict: 'up' | 'down' | null; decidedBy: string | null; decidedAt: string | null; hasOpenCallback: boolean }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/quality`,
    ),
  resolveJobCallback: (eventId: string, id: number) =>
    fetchApi<{ ok: boolean }>(`/calendar/jobs/${encodeURIComponent(eventId)}/callbacks/${id}/resolve`, { method: 'POST' }),
  // Review + referral engine
  getReviewReferralPreview: () =>
    fetchApi<{
      notConfigured: boolean;
      settings: { googleReviewUrl: string | null; giverCredit: string; receiverDiscount: string; minJobValue: number };
      rows: {
        eventId: string; firstName: string; touch: 'review' | 'referral'; channel: string | null;
        goesTo: string | null; status: 'upcoming' | 'sent' | 'skipped' | 'failed'; reason?: string | null;
        sentAt?: string | null; sendAt?: string | null; message?: string | null; subject?: string | null; clicks?: number | null;
      }[];
    }>('/review-referral/preview'),
  cancelReviewReferral: (eventId: string, touch: 'review' | 'referral') =>
    fetchApi<{ ok: boolean }>('/review-referral/cancel', { method: 'POST', body: JSON.stringify({ eventId, touch }) }),
  restoreReviewReferral: (eventId: string, touch: 'review' | 'referral') =>
    fetchApi<{ ok: boolean }>('/review-referral/restore', { method: 'POST', body: JSON.stringify({ eventId, touch }) }),
  updateReviewReferralSettings: (patch: Record<string, string>) =>
    fetchApi<any>('/review-referral/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  listReferrals: () =>
    fetchApi<{
      id: number; referrerName: string | null; referredName: string | null; referredContact: string | null;
      status: 'mentioned' | 'quoted' | 'completed' | 'credited'; referredJobValue: number | null; notes: string | null; updatedAt: string;
    }[]>('/referrals'),
  createReferral: (data: { referrerName?: string; referredName?: string; referredContact?: string; notes?: string }) =>
    fetchApi<any>('/referrals', { method: 'POST', body: JSON.stringify(data) }),
  advanceReferral: (id: number, data: { status: string; referredJobValue?: number; notes?: string }) =>
    fetchApi<any>(`/referrals/${id}/advance`, { method: 'POST', body: JSON.stringify(data) }),
  // Undo an accidental completion — move the job back to BOOKED (does NOT void
  // the Xero invoice; that's done in Xero).
  calendarRevertToBooked: (eventId: string) =>
    fetchApi<{ success: boolean; error?: string; docNumber?: string | null }>(
      `/calendar/jobs/${encodeURIComponent(eventId)}/revert-to-booked`,
      { method: 'POST' },
    ),
  billingSchedule: (data: {
    contactId: string;
    startTime: string;
    durationMins?: number;
    summary?: string;
    location?: string | null;
    description?: string | null;
    docId?: string | null;
    docType?: 'quote' | 'invoice' | null;
  }) =>
    fetchApi<{
      bookingId: string;
      start: string;
      end: string;
      days?: number;
      calendar?: { success: boolean; htmlLink?: string; error?: string };
    }>('/billing/schedule', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Campaigns + referral partners
  getReferralPartners: () => fetchApi<any[]>('/referral-partners'),
  createReferralPartner: (data: any) =>
    fetchApi<any>('/referral-partners', { method: 'POST', body: JSON.stringify(data) }),
  updateReferralPartner: (id: number, data: any) =>
    fetchApi<any>(`/referral-partners/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteReferralPartner: (id: number) =>
    fetchApi<{ ok: boolean }>(`/referral-partners/${id}`, { method: 'DELETE' }),
  // Equipment service log
  getServiceLog: () =>
    fetchApi<{ entries: ServiceLogEntry[] }>(`/service-log`),
  createServiceLogEntry: (data: ServiceLogInput) =>
    fetchApi<ServiceLogEntry>(`/service-log`, { method: 'POST', body: JSON.stringify(data) }),
  updateServiceLogEntry: (id: number, data: Partial<ServiceLogInput>) =>
    fetchApi<ServiceLogEntry>(`/service-log/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteServiceLogEntry: (id: number) =>
    fetchApi<{ ok: boolean }>(`/service-log/${id}`, { method: 'DELETE' }),
  getMaintenanceSchedule: () =>
    fetchApi<{ items: MaintenanceScheduleItem[] }>(`/maintenance-schedule`),
  createMaintenanceItem: (data: MaintenanceScheduleInput) =>
    fetchApi<MaintenanceScheduleItem>(`/maintenance-schedule`, { method: 'POST', body: JSON.stringify(data) }),
  updateMaintenanceItem: (id: number, data: Partial<MaintenanceScheduleInput>) =>
    fetchApi<MaintenanceScheduleItem>(`/maintenance-schedule/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteMaintenanceItem: (id: number) =>
    fetchApi<{ ok: boolean }>(`/maintenance-schedule/${id}`, { method: 'DELETE' }),
  getMaintenanceStatus: () =>
    fetchApi<MaintenanceStatus>(`/maintenance-status`),
  setEngineUsagePct: (pct: number) =>
    fetchApi<{ usagePct: number }>(`/maintenance-status/usage`, { method: 'PUT', body: JSON.stringify({ pct }) }),
  logReferralContact: (id: number) =>
    fetchApi<any>(`/referral-partners/${id}/log-contact`, { method: 'POST' }),
  getReferralActivity: (dormantDays?: number) =>
    fetchApi<{ rows: ReferralActivityRow[] }>(
      `/referral-activity${dormantDays ? `?dormantDays=${dormantDays}` : ''}`,
    ),
  backfillReferralActivity: (since?: string | null) =>
    fetchApi<{ scanned: number; matched: number; ambiguous: number; unmatched: number; alreadyLinked: number }>(
      '/referral-activity/backfill',
      { method: 'POST', body: JSON.stringify({ since: since || null }) },
    ),
  getDormantCustomers: (days: number, maxDays?: number | null) =>
    fetchApi<{ thresholdDays: number; maxDays: number | null; recipients: any[] }>(
      `/campaigns/audience/dormant-customers?days=${days}${maxDays ? `&maxDays=${maxDays}` : ''}`,
    ),
  setContactOptout: (customerId: number, channel: 'email' | 'sms', optedOut: boolean) =>
    fetchApi<{ channel: string; optedOut: boolean; value: string | null }>(
      `/campaigns/contact-optout`,
      { method: 'POST', body: JSON.stringify({ customerId, channel, optedOut }) },
    ),
  getDormantReferrers: (days: number) =>
    fetchApi<{ thresholdDays: number; recipients: any[] }>(
      `/campaigns/audience/dormant-referrers?days=${days}`,
    ),

  // Campaign templates
  getCampaignTemplates: () => fetchApi<any[]>('/campaign-templates'),
  createCampaignTemplate: (data: any) =>
    fetchApi<any>('/campaign-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateCampaignTemplate: (id: number, data: any) =>
    fetchApi<any>(`/campaign-templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCampaignTemplate: (id: number) =>
    fetchApi<any>(`/campaign-templates/${id}`, { method: 'DELETE' }),
  testCampaignTemplate: (id: number, to: string, name?: string) =>
    fetchApi<{ to: string; status: string; error?: string }[]>(`/campaign-templates/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({ to, name }),
    }),
  previewCampaignTemplate: (id: number, name?: string) =>
    fetchApi<{ subject: string; html: string }>(
      `/campaign-templates/${id}/preview${name ? `?name=${encodeURIComponent(name)}` : ''}`,
    ),

  // Campaigns
  getCampaigns: () => fetchApi<any[]>('/campaigns'),
  // Evergreen auto win-back: dry-run preview of who it would reach next (never sends).
  getAutoWinbackPreview: () =>
    fetchApi<{
      window: boolean;
      remainingToday: number;
      candidates?: { customerId: number; name: string | null; email: string | null; mobile: string | null; channel: 'sms' | 'email' | 'none'; message: string; daysSince: number }[];
    }>('/campaigns/auto-winback/preview'),
  cancelAutoWinback: (customerId: number) =>
    fetchApi<{ ok: boolean }>('/campaigns/auto-winback/cancel', { method: 'POST', body: JSON.stringify({ customerId }) }),
  restoreAutoWinback: (customerId: number) =>
    fetchApi<{ ok: boolean }>('/campaigns/auto-winback/restore', { method: 'POST', body: JSON.stringify({ customerId }) }),
  createCampaign: (data: any) =>
    fetchApi<any>('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  getCampaign: (id: number) => fetchApi<any>(`/campaigns/${id}`),
  buildCampaign: (id: number) =>
    fetchApi<{ recipients: number; withoutEmail: number; suppressed: number }>(`/campaigns/${id}/build`, {
      method: 'POST',
    }),
  scheduleCampaign: (id: number, scheduledAt: string) =>
    fetchApi<any>(`/campaigns/${id}/schedule`, { method: 'POST', body: JSON.stringify({ scheduledAt }) }),
  cancelCampaign: (id: number) => fetchApi<any>(`/campaigns/${id}/cancel`, { method: 'POST' }),
  reopenCampaign: (id: number) => fetchApi<any>(`/campaigns/${id}/reopen`, { method: 'POST' }),
  sendCampaign: (id: number, dryRun: boolean) =>
    fetchApi<{ sent: number; failed: number; skipped: number }>(`/campaigns/${id}/send`, {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    }),
  getCampaignStats: (id: number) => fetchApi<Record<string, number>>(`/campaigns/${id}/stats`),
  testCampaignSend: (id: number, to: string, recipientId?: number) =>
    fetchApi<{ sampleName: string | null; results: { to: string; status: string; error?: string }[] }>(
      `/campaigns/${id}/test-send`,
      { method: 'POST', body: JSON.stringify({ to, recipientId }) },
    ),

  // Quotes module + Xero contact detail
  getQuotes: (status?: string) => fetchApi<any[]>(`/quotes${status ? `?status=${status}` : ''}`),
  remindQuote: (xeroId: string, dryRun: boolean) =>
    fetchApi<{ status: string; reason?: string }>(`/quotes/${xeroId}/remind`, {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    }),
  getXeroContact: (contactId: string) => fetchApi<any>(`/xero-contact/${contactId}`),
};

// Deep-links into the connected Xero org (staff are logged in there).
export const xeroLink = {
  invoice: (id: string) => `https://go.xero.com/app/invoicing/view/${id}`,
  quote: (id: string) => `https://go.xero.com/app/quotes/view/${id}`,
};

export default api;
